import { useCallback, useEffect, useRef, useState } from 'react';
import { scheduleDeferredWork } from '../../utils/deferredWork';
import {
  incidentsApi,
  type Incident,
  type IncidentGroup,
  type IncidentKpis,
  type IncidentOpsQueuesResponse,
  type IncidentReviewOutcomeRollupResponse,
  type IncidentSeverity,
} from '../../services/incidentsApi';
import { ensureUiScope } from '../../services/api';
import type { IncidentWorkflowStatusFilter } from './useIncidentsFilters';
import {
  filterGroupsForOwnershipQueue,
  type IncidentOpsQueueFilter,
} from '../../domain/incidents/incidentOwnershipQueueFilter';
import {
  REVIEW_QUEUE_FALLBACK_TIMEOUT_MS,
  REVIEW_QUEUE_LIVE_REFRESH_TIMEOUT_MS,
  REVIEW_QUEUE_PERSISTED_TIMEOUT_MS,
  classifyReviewQueueFailure,
  mergeAuthoritativePersistedGroups,
  reviewQueueFailureUserMessage,
  shouldAttemptLegacyGroupsFallback,
} from '../../domain/incidents/reviewQueueFetchPolicy';
import type { IncidentTableMode } from './useIncidentsFilters';

type UseIncidentsDataArgs = {
  tabValue: number;
  tableMode: IncidentTableMode;
  filterIncidentType: string;
  filterSeverity: IncidentSeverity | '';
  filterZoneId: string;
  filterDeviceId: string;
  filterCameraId: string;
  filterIncidentStatus: '' | 'OPEN' | 'ACKED' | 'IN_REVIEW' | 'CLOSED' | 'SUPPRESSED';
  filterWorkflowStatus: IncidentWorkflowStatusFilter;
  filterOpsQueue: IncidentOpsQueueFilter;
  filterOpsAssignee: string;
  operatorActorId: string;
  timeRange: {
    fromDate?: string;
    toDate?: string;
  };
  searchIncidentId: string;
  onLoadError: (message: string | null) => void;
  loadEdgeSuppressionsForDevices: (deviceIds: string[]) => Promise<void> | void;
};

type FetchDataOptions = {
  showLoading?: boolean;
  /** When true, fetch live groups directly and skip the deferred refresh (e.g. background poll). */
  skipPersistedRefresh?: boolean;
};

const GROUPS_TABLE_LIMIT = 100;

/**
 * Individual Observations default is every qualified episode.
 * The Active queue tab (tabValue 1) must not send status=open.
 * Open is sent only when the status filter explicitly selects it.
 * Completed (tabValue 2) stays an explicit closed filter.
 * Patterns keep their own groups status and are not decided here.
 */
export function rawSafetyEpisodeListStatus(args: {
  tabValue: number;
  filterIncidentStatus: string;
  filterWorkflowStatus: string;
}): string | undefined {
  const explicit = String(args.filterIncidentStatus || '').trim();
  if (explicit) {
    return explicit.toUpperCase() === 'OPEN' ? 'open' : explicit;
  }
  if (args.filterWorkflowStatus === 'Reviewed') return undefined;
  if (args.tabValue === 2) return 'closed';
  return undefined;
}

const _groupLastSeenMs = (group: Partial<IncidentGroup> | null | undefined): number => {
  const raw = String(
    group?.last_seen_at
      || group?.last_seen
      || (group as any)?.updated_at
      || (group as any)?.created_at
      || '',
  ).trim();
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function sortGroupedIncidentsByLastSeen<T extends Partial<IncidentGroup>>(groups: T[]): T[] {
  return [...(groups || [])].sort((left, right) => {
    const diff = _groupLastSeenMs(right) - _groupLastSeenMs(left);
    if (diff !== 0) return diff;
    return String((right as any)?.group_key || '').localeCompare(String((left as any)?.group_key || ''));
  });
}

export function useIncidentsData({
  tabValue,
  tableMode,
  filterIncidentType,
  filterSeverity,
  filterZoneId,
  filterDeviceId,
  filterCameraId,
  filterIncidentStatus,
  filterWorkflowStatus,
  filterOpsQueue,
  filterOpsAssignee,
  operatorActorId,
  timeRange,
  searchIncidentId,
  onLoadError,
  loadEdgeSuppressionsForDevices,
}: UseIncidentsDataArgs) {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [filteredIncidents, setFilteredIncidents] = useState<Incident[]>([]);
  const [incidentsTotal, setIncidentsTotal] = useState<number | null>(null);
  const [kpis, setKpis] = useState<IncidentKpis | null>(null);
  const [opsQueues, setOpsQueues] = useState<IncidentOpsQueuesResponse | null>(null);
  const [reviewOutcomeRollup, setReviewOutcomeRollup] = useState<IncidentReviewOutcomeRollupResponse | null>(null);
  const [groups, setGroups] = useState<IncidentGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const incidentsMainFetchGenRef = useRef(0);
  const incidentsMainFetchInFlightRef = useRef(false);
  const incidentsPendingFetchOptsRef = useRef<FetchDataOptions | null>(null);
  const fetchDataRef = useRef<((opts?: FetchDataOptions) => Promise<void>) | null>(null);
  const authoritativeGroupedRef = useRef<{ contextKey: string; items: IncidentGroup[] } | null>(null);

  const fetchData = useCallback(async (opts?: FetchDataOptions) => {
    if (incidentsMainFetchInFlightRef.current) {
      incidentsPendingFetchOptsRef.current = {
        ...(incidentsPendingFetchOptsRef.current || {}),
        ...(opts || {}),
      };
      return;
    }
    incidentsMainFetchInFlightRef.current = true;
    const showLoading = opts?.showLoading !== false;
    if (showLoading) setLoading(true);
    onLoadError(null);
    const gen = ++incidentsMainFetchGenRef.current;
    setReviewOutcomeRollup(null);
    try {
      let scope: { orgId?: string; siteId?: string } = {};
      try {
        scope = ensureUiScope();
      } catch {
        scope = {};
      }
      const episodeListStatus = rawSafetyEpisodeListStatus({
        tabValue,
        filterIncidentStatus,
        filterWorkflowStatus,
      });
      const kpiParams = {
        from_date: timeRange.fromDate,
        to_date: timeRange.toDate,
      };
      const groupsFetchStatus: 'open' | 'closed' | 'all' =
        tabValue === 2 || filterIncidentStatus === 'CLOSED' || filterIncidentStatus === 'SUPPRESSED'
          ? 'closed'
          : tabValue === 1
            ? 'open'
            : filterIncidentStatus
              ? 'open'
              : 'all';

      const groupsParams = {
        status: groupsFetchStatus,
        from_date: timeRange.fromDate,
        to_date: timeRange.toDate,
        incident_type: filterIncidentType || undefined,
        severity: (filterSeverity as any) || undefined,
        zone_id: filterZoneId || undefined,
        device_id: filterDeviceId || undefined,
        camera_id: filterCameraId || undefined,
        limit: GROUPS_TABLE_LIMIT,
      };

      const queueActor = String(filterOpsAssignee || operatorActorId || '').trim() || undefined;
      const reviewQueueContextKey = JSON.stringify({
        orgId: scope.orgId || '',
        siteId: scope.siteId || '',
        tabValue,
        tableMode,
        filterIncidentType,
        filterSeverity,
        filterZoneId,
        filterDeviceId,
        filterCameraId,
        filterIncidentStatus,
        filterWorkflowStatus,
        filterOpsQueue,
        filterOpsAssignee,
        operatorActorId,
        fromDate: timeRange.fromDate || '',
        toDate: timeRange.toDate || '',
        groupsFetchStatus,
      });

      const applyGroupedItems = (items: IncidentGroup[]) => {
        const filteredGroups = filterGroupsForOwnershipQueue(
          items || [],
          filterOpsQueue,
          operatorActorId,
        );
        setGroups(sortGroupedIncidentsByLastSeen(filteredGroups));
        setIncidents([]);
        setIncidentsTotal(null);
        const devs = filterDeviceId
          ? [filterDeviceId]
          : (filteredGroups || []).map((x: any) => x?.device_id).filter(Boolean);
        void loadEdgeSuppressionsForDevices(devs);
      };

      const refreshLiveGroups = (authoritativeItems: IncidentGroup[]) => {
        if (opts?.skipPersistedRefresh) return;
        scheduleDeferredWork(() => {
          if (gen !== incidentsMainFetchGenRef.current) return;
          void (async () => {
            try {
              // Bounded live refresh only — never another full 30s heavy query.
              // prefer_persisted=false is narrower (created_at window) and is not
              // allowed to replace the persisted membership, including on HTTP 200 [].
              const liveRes = await incidentsApi.listIncidentGroups(
                {
                  ...groupsParams,
                  prefer_persisted: false,
                },
                { timeoutMs: REVIEW_QUEUE_LIVE_REFRESH_TIMEOUT_MS },
              );
              if (gen !== incidentsMainFetchGenRef.current) return;
              applyGroupedItems(
                mergeAuthoritativePersistedGroups(authoritativeItems, liveRes.items || []),
              );
            } catch {
              // keep the persisted first-paint rows already on screen
            }
          })();
        }, { timeoutMs: 1800 });
      };

      scheduleDeferredWork(() => {
        if (gen !== incidentsMainFetchGenRef.current) return;
        void (async () => {
          try {
            const opsQueuesRes = await incidentsApi
              .getOpsQueues({
                assignee: queueActor,
                limit: 25,
              })
              .catch(() => null);
            if (gen !== incidentsMainFetchGenRef.current) return;
            if (opsQueuesRes) setOpsQueues(opsQueuesRes);
          } catch {
            // non-blocking
          }
        })();
      });

      const loadKpisDeferred = () => {
        void (async () => {
          try {
            const kpisRes = await incidentsApi.getKpis(kpiParams);
            if (gen !== incidentsMainFetchGenRef.current) return;
            setKpis(kpisRes);
          } catch {
            // KPI tiles are non-blocking for the main queue table.
          }
        })();
      };

      const loadReviewOutcomeRollupDeferred = () => {
        const fromTs = timeRange.fromDate;
        const toTs = timeRange.toDate;
        if (!fromTs || !toTs) return;
        scheduleDeferredWork(() => {
          if (gen !== incidentsMainFetchGenRef.current) return;
          void (async () => {
            try {
              const reviewOutcomeRollupRes = await incidentsApi.getReviewOutcomeRollup({
                org_id: scope.orgId || undefined,
                site_id: scope.siteId || undefined,
                from_ts: fromTs,
                to_ts: toTs,
                incident_type: filterIncidentType || undefined,
                severity: (filterSeverity as any) || undefined,
                zone_id: filterZoneId || undefined,
                device_id: filterDeviceId || undefined,
                camera_id: filterCameraId || undefined,
              });
              if (gen !== incidentsMainFetchGenRef.current) return;
              setReviewOutcomeRollup(reviewOutcomeRollupRes);
            } catch {
              // Review throughput is non-blocking for the main queue table.
            }
          })();
        });
      };

      if (tableMode === 'grouped') {
        if (opts?.skipPersistedRefresh) {
          const liveRes = await incidentsApi.listIncidentGroups(
            {
              ...groupsParams,
              prefer_persisted: false,
            },
            { timeoutMs: REVIEW_QUEUE_FALLBACK_TIMEOUT_MS },
          );
          if (gen !== incidentsMainFetchGenRef.current) return;
          const snapshot = authoritativeGroupedRef.current;
          if (snapshot && snapshot.contextKey === reviewQueueContextKey) {
            applyGroupedItems(
              mergeAuthoritativePersistedGroups(snapshot.items, liveRes.items || []),
            );
          } else if (!snapshot && (liveRes.items || []).length > 0) {
            // Review bootstrap has no persisted snapshot yet. A non-empty live
            // body can paint; an empty one must not clear rows from another context.
            applyGroupedItems(liveRes.items || []);
          }
        } else {
          try {
            const persistedRes = await incidentsApi.listIncidentGroups(
              {
                ...groupsParams,
                prefer_persisted: true,
              },
              { timeoutMs: REVIEW_QUEUE_PERSISTED_TIMEOUT_MS },
            );
            if (gen !== incidentsMainFetchGenRef.current) return;
            const authoritativeItems = persistedRes.items || [];
            authoritativeGroupedRef.current = {
              contextKey: reviewQueueContextKey,
              items: authoritativeItems,
            };
            applyGroupedItems(authoritativeItems);
            refreshLiveGroups(authoritativeItems);
          } catch (persistedErr) {
            const kind = classifyReviewQueueFailure(persistedErr);
            if (!shouldAttemptLegacyGroupsFallback(kind)) {
              // EPIC290: no sequential 30s+30s cascade under timeout/capacity pressure.
              onLoadError(reviewQueueFailureUserMessage(kind));
              // Preserve last usable groups already on screen (do not clear to 0–0 of 0).
              return;
            }
            try {
              const liveRes = await incidentsApi.listIncidentGroups(
                {
                  ...groupsParams,
                  prefer_persisted: false,
                },
                { timeoutMs: REVIEW_QUEUE_FALLBACK_TIMEOUT_MS },
              );
              if (gen !== incidentsMainFetchGenRef.current) return;
              const fallbackItems = liveRes.items || [];
              authoritativeGroupedRef.current = {
                contextKey: reviewQueueContextKey,
                items: fallbackItems,
              };
              applyGroupedItems(fallbackItems);
            } catch (liveErr) {
              const liveKind = classifyReviewQueueFailure(liveErr);
              onLoadError(reviewQueueFailureUserMessage(liveKind));
              return;
            }
          }
        }

        loadKpisDeferred();
        loadReviewOutcomeRollupDeferred();
      } else {
        const incidentsRes = await incidentsApi.listSafetyEpisodeObservations({
          status: episodeListStatus,
          severity: (filterSeverity as any) || undefined,
          limit: 100,
          offset: 0,
          fromDate: timeRange.fromDate,
          toDate: timeRange.toDate,
          incidentType: filterIncidentType || undefined,
          zoneId: filterZoneId || undefined,
          deviceId: filterDeviceId || undefined,
          cameraId: filterCameraId || undefined,
          skip_total: false,
        });
        if (gen !== incidentsMainFetchGenRef.current) return;
        setIncidents(incidentsRes.incidents || []);
        setIncidentsTotal(
          typeof incidentsRes.count === 'number' && Number.isFinite(incidentsRes.count)
            ? Math.max(0, Math.trunc(incidentsRes.count))
            : (incidentsRes.incidents || []).length,
        );
        const devs = filterDeviceId
          ? [filterDeviceId]
          : (incidentsRes.incidents || []).map((x: any) => x?.device_id).filter(Boolean);
        void loadEdgeSuppressionsForDevices(devs);
        loadKpisDeferred();
        loadReviewOutcomeRollupDeferred();
        // Keep windowed groups for Investigate High severity KPI (in-window
        // HIGH+CRITICAL observation volume). Do not clear last usable groups,
        // and do not use unassigned_high_risk backlog for this tile.
        scheduleDeferredWork(() => {
          if (gen !== incidentsMainFetchGenRef.current) return;
          void (async () => {
            try {
              const kpiGroupsRes = await incidentsApi.listIncidentGroups(
                {
                  status: groupsFetchStatus,
                  from_date: timeRange.fromDate,
                  to_date: timeRange.toDate,
                  limit: GROUPS_TABLE_LIMIT,
                  prefer_persisted: true,
                },
                { timeoutMs: REVIEW_QUEUE_PERSISTED_TIMEOUT_MS },
              );
              if (gen !== incidentsMainFetchGenRef.current) return;
              setGroups(sortGroupedIncidentsByLastSeen(kpiGroupsRes?.items || []));
            } catch {
              // Keep last usable groups already on screen for the KPI row.
            }
          })();
        });
      }
    } catch (err: any) {
      const kind = classifyReviewQueueFailure(err);
      onLoadError(
        err?.response?.data?.error
          || reviewQueueFailureUserMessage(kind)
          || 'Incidents list failed to load',
      );
      // EPIC290: never render a genuine empty queue on transport/backend failure.
      // Keep last usable incidents/groups; only clear when we have never painted.
      setIncidents((prev) => (prev.length ? prev : []));
      setGroups((prev) => (prev.length ? prev : []));
    } finally {
      incidentsMainFetchInFlightRef.current = false;
      setLoading(false);
      const replayOpts = incidentsPendingFetchOptsRef.current;
      if (replayOpts) {
        incidentsPendingFetchOptsRef.current = null;
        window.setTimeout(() => {
          void fetchDataRef.current?.(replayOpts);
        }, 0);
      }
    }
  }, [
    tabValue,
    filterIncidentType,
    filterSeverity,
    filterZoneId,
    filterDeviceId,
    filterCameraId,
    filterIncidentStatus,
    filterWorkflowStatus,
    filterOpsQueue,
    filterOpsAssignee,
    operatorActorId,
    tableMode,
    timeRange.fromDate,
    timeRange.toDate,
    onLoadError,
    loadEdgeSuppressionsForDevices,
  ]);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    const q = String(searchIncidentId || '').trim().toLowerCase();
    if (!q) {
      setFilteredIncidents(incidents);
      return;
    }
    setFilteredIncidents(
      (incidents || []).filter((incident) => {
        const incidentId = (incident.incident_id || '').toLowerCase();
        const episodeId = String(incident.episode_id || '').toLowerCase();
        return incidentId.includes(q) || episodeId.includes(q);
      })
    );
  }, [incidents, searchIncidentId]);

  const clearLoading = useCallback(() => {
    setLoading(false);
  }, []);

  return {
    incidents,
    setIncidents,
    filteredIncidents,
    incidentsTotal,
    kpis,
    opsQueues,
    reviewOutcomeRollup,
    groups,
    loading,
    fetchData,
    clearLoading,
  };
}
